/**
 * Real self-fetching Leave-requests queue hook (#23310). Mirrors
 * `use_expenses.ts`'s established pattern: the real bearer client
 * (`getTrpcClient()`, `lib/trpc.ts`), the shared `useOAuthEntityContext()`
 * helper, DI-testable fetch/mutation functions, an explicit state machine
 * (`loading` / `no_entity` / `entitlement_required` / `error` / `ready`), no
 * mock fallback anywhere.
 *
 * ── Two real reads, one shared entity context ──────────────────────────
 *
 *  - `payroll.listLeaveRequests` — the requests themselves. No `status`
 *    filter is passed: the queue screen filters client-side by tab so all
 *    three tabs (pending/approved/rejected) share ONE fetch, and their
 *    counters can be shown together without a second round-trip. The
 *    per-entity total is bounded by real-world leave volume (mirrors the
 *    same "no pagination UI" stance every other WagePro list read here
 *    takes).
 *  - `payroll.listEmployees` — resolves `employee_id` → display name. The
 *    same read `use_expenses.ts` and `use_pay_runs.ts` already reuse.
 *
 * `deriveLeaveRequestsFromRecords` (`wagepro_store_leave.ts`) is the pure
 * adapter both feed into.
 *
 * ── entitlement_required vs error ──────────────────────────────────────
 *
 * The 4 leave procedures are gated by `requirePayrollAddon` (they run on
 * `payrollWriteProcedure` / `payrollEntityProcedure`, which chain that
 * middleware; see `apps/api/src/trpc/routers/payroll.ts`) — so a missing
 * `payroll` add-on grant resolves `entitlement_required`, not `error`
 * (classified via `isPayrollAddonRequiredError`, same as `use_pay_runs.ts`).
 * Every OTHER FORBIDDEN (missing OAuth scope, null-audience token, no entity
 * access) resolves the generic `error` state — same "don't mask a real auth
 * bug behind a misleading upgrade prompt" stance the sibling hooks take.
 *
 * ── Approve / Reject write path ────────────────────────────────────────
 *
 * `approve`/`reject` are `undefined` (hiding the affordance entirely, never
 * a button that would 403) unless BOTH of:
 *
 *   - the read resolved `ready`, AND
 *   - `isWriteCapableGrant(grant)` (`use_pay_runs.ts`) — a read-only OAuth
 *     token never sees the buttons.
 *
 * The backend's own `approveLeave`/`rejectLeave` procedures ADDITIONALLY
 * gate on the firm role (`MANAGER_ROLES = {'owner','admin'}`, see
 * `apps/api/src/trpc/routers/payroll.ts:1050`) via a control-plane read.
 * That check runs at request time and is defence-in-depth on top of this
 * scope-gate: a WagePro `payroll:employer:write` token minted for a caller
 * who is NOT a firm admin/owner will still see the buttons but the mutation
 * itself returns FORBIDDEN, classified below as `LeaveActionFailure.forbidden`
 * and rendered as a helpful one-line inline error rather than a raw toast.
 * We deliberately do NOT gate button visibility on the firm role client-side
 * because no OAuth-reachable read exposes it — mirroring how the expenses
 * queue classifies its own backend `requireExpensesRole` mismatches.
 *
 * A bare BAD_REQUEST reaching approve because the leave balance is
 * insufficient (`payroll.ts:2758`, `Insufficient … leave balance:`) is
 * classified as `LeaveActionFailure.insufficient_balance`. A CONFLICT from
 * the compare-and-swap that guards concurrent approvals against a stale
 * balance (`payroll.ts:2813`, `Leave balance changed since this request was
 * checked`) is classified as `LeaveActionFailure.stale_balance` — the
 * caller can safely refresh and retry (the request row is untouched).
 */
import * as React from 'react';
import {
  PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE,
  extractLedgerCode,
  getTrpcClient,
  isForbiddenTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import {
  deriveLeaveRequestsFromRecords,
  type LeaveEmployeeLookup,
  type LeaveRequest,
  type LeaveStatus,
  type RealLeaveRequestRecord,
} from './wagepro_store_leave.js';

/** Input every `payroll.*` call in this module needs to identify the entity. */
export interface LeaveFetchInput {
  firm_id: string;
  entity_id: string;
}

/** The two real reads {@link fetchLeaveRequestsBundle} needs, isolated behind an injectable seam. */
export interface LeaveFetchDeps {
  listRequests: (
    input: LeaveFetchInput,
  ) => Promise<{ requests: RealLeaveRequestRecord[] }>;
  listEmployees: (
    input: LeaveFetchInput,
  ) => Promise<{ employees: LeaveEmployeeLookup[] }>;
}

function defaultLeaveFetchDeps(): LeaveFetchDeps {
  const client = getTrpcClient();
  return {
    listRequests: (input) => client.payroll.listLeaveRequests.query(input),
    listEmployees: (input) => client.payroll.listEmployees.query(input),
  };
}

/** How the real leave-requests bundle can fail, classified by {@link isPayrollAddonRequiredError}. */
export type LeaveLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): LeaveLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

export type LeaveFetchResult =
  | {
      outcome: 'success';
      requests: LeaveRequest[];
      employees: LeaveEmployeeLookup[];
    }
  | { outcome: 'failure'; failure: LeaveLoadFailure };

/**
 * Resolves the entity's real leave-request queue, or classifies why it
 * couldn't. Exported (and DI'd via `deps`) so the classification is
 * unit-testable against a fake client, mirroring `fetchExpensesBundle`.
 */

export async function fetchLeaveRequestsBundle(
  input: LeaveFetchInput,
  deps?: LeaveFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<LeaveFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveFetchDeps();
    const [{ requests }, { employees }] = await Promise.all([
      resolvedDeps.listRequests(input),
      resolvedDeps.listEmployees(input),
    ]);
    const derived = deriveLeaveRequestsFromRecords(requests, employees);
    return { outcome: 'success', requests: derived, employees };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real leave-request queue:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export type LeaveState =
  | { status: 'loading' }
  /** The OAuth grant has no bound entity — structurally unreachable in practice (`main.tsx`'s `AuthGate` gates the whole app on this first), handled defensively anyway. */
  | { status: 'no_entity' }
  /** The entity is missing the `payroll` add-on entitlement. NEVER render a mock queue here. */
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; requests: LeaveRequest[] };

/**
 * Pure derivation, exported separately so it is unit-testable without
 * rendering React — mirrors `resolveExpensesState`'s precedence exactly.
 */
export function resolveLeaveState(input: {
  entityContext: OAuthEntityContext;
  requests: LeaveRequest[] | undefined;
  failure: LeaveLoadFailure | undefined;
}): LeaveState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.requests !== undefined) {
    return { status: 'ready', requests: input.requests };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path: approve / reject ───────────────────────────────────────────

/** How an `approveLeave` / `rejectLeave` mutation can fail, classified for a human-readable state rather than a raw error toast. */
export type LeaveActionFailure =
  | 'entitlement_required'
  | 'forbidden'
  /** `NOT_FOUND` from the backend — the request was deleted or never existed. Distinguished from `error` so the UI can prompt a refresh instead of a retry. */
  | 'not_found'
  /** `BAD_REQUEST: Leave request is not pending` — someone else already approved or rejected. Distinguished from `error` so the UI can prompt a refresh. */
  | 'already_decided'
  | 'insufficient_balance'
  | 'stale_balance'
  /** `BAD_REQUEST` + `LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE` -- the worker was switched to contractor after requesting leave. */
  | 'contractor_not_eligible'
  | 'error';

const INSUFFICIENT_BALANCE_PREFIX = 'Insufficient';
const STALE_BALANCE_PREFIX = 'Leave balance changed';
const NOT_PENDING_MESSAGE = 'Leave request is not pending';

function classifyActionFailure(err: unknown): LeaveActionFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (extractLedgerCode(err) === PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE) {
    return 'contractor_not_eligible';
  }
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: { code?: unknown } }).data;
    const code = data?.code;
    const message = err instanceof Error ? err.message : '';
    if (code === 'NOT_FOUND') return 'not_found';
    if (code === 'BAD_REQUEST' && message === NOT_PENDING_MESSAGE) {
      return 'already_decided';
    }
    if (
      code === 'BAD_REQUEST' &&
      message.startsWith(INSUFFICIENT_BALANCE_PREFIX)
    ) {
      return 'insufficient_balance';
    }
    if (code === 'CONFLICT' && message.startsWith(STALE_BALANCE_PREFIX)) {
      return 'stale_balance';
    }
  }
  return 'error';
}

export type LeaveActionResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: LeaveActionFailure };

/** The two real mutations {@link approveLeaveRequest} / {@link rejectLeaveRequest} need, isolated behind an injectable seam. */
export interface LeaveActionDeps {
  approve: (
    input: LeaveFetchInput & { request_id: string },
  ) => Promise<unknown>;
  reject: (
    input: LeaveFetchInput & {
      request_id: string;
      rejection_reason?: string;
    },
  ) => Promise<unknown>;
}

function defaultLeaveActionDeps(): LeaveActionDeps {
  const client = getTrpcClient();
  return {
    approve: (input) => client.payroll.approveLeave.mutate(input),
    reject: (input) => client.payroll.rejectLeave.mutate(input),
  };
}

/**
 * Calls the real `payroll.approveLeave`. Never throws — every failure mode
 * resolves `{outcome: 'failure', failure}` with one actionable
 * `console.error`, mirroring `approveExpenseClaim`'s never-throw contract
 * (including resolving `deps` INSIDE the try, so a synchronous
 * `getTrpcClient()` throw can't escape as an unhandled rejection).
 */
export async function approveLeaveRequest(
  request_id: string,
  entityInput: LeaveFetchInput,
  deps?: LeaveActionDeps,
): Promise<LeaveActionResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveActionDeps();
    await resolvedDeps.approve({ ...entityInput, request_id });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave: approve failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

/** Calls the real `payroll.rejectLeave`. Same never-throw contract as {@link approveLeaveRequest}. `rejection_reason` is OPTIONAL on the backend (`z.string().optional()`), so `undefined` means "no reason given". */
export async function rejectLeaveRequest(
  request_id: string,
  rejection_reason: string | undefined,
  entityInput: LeaveFetchInput,
  deps?: LeaveActionDeps,
): Promise<LeaveActionResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveActionDeps();
    await resolvedDeps.reject({
      ...entityInput,
      request_id,
      ...(rejection_reason !== undefined ? { rejection_reason } : {}),
    });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave: reject failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

/** Bucket counts, one per status, for the queue's tab strip. */
export function count_by_status(
  requests: readonly LeaveRequest[],
): Record<LeaveStatus, number> {
  const counts: Record<LeaveStatus, number> = {
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  };
  for (const request of requests) {
    counts[request.status] += 1;
  }
  return counts;
}

export function filter_by_status(
  requests: readonly LeaveRequest[],
  status: LeaveStatus,
): LeaveRequest[] {
  return requests.filter((request) => request.status === status);
}

// ── Write path: cancel ─────────────────────────────────────────────────────

/**
 * How `payroll.cancelLeave` can fail, classified for inline rendering.
 * Mirrors `LeaveActionFailure` — separate type because cancel can fail
 * for different reasons (a pending OR approved request can be cancelled,
 * but an approved request of an unpaid type overlapping a pay run will
 * `BAD_REQUEST` with a specific message).
 */
export type LeaveCancelFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  /** `BAD_REQUEST: Cannot cancel a leave request with status …` */
  | 'already_terminal'
  /** `BAD_REQUEST: Cannot cancel an approved unpaid leave request that overlaps a completed pay run.` */
  | 'pay_run_overlap'
  | 'error';

const TERMINAL_STATUS_PREFIX = 'Cannot cancel a leave request with status';
const PAY_RUN_OVERLAP_PREFIX =
  'Cannot cancel an approved unpaid leave request that overlaps';

function classifyCancelFailure(err: unknown): LeaveCancelFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: { code?: unknown } }).data;
    const code = data?.code;
    const message = err instanceof Error ? err.message : '';
    if (code === 'NOT_FOUND') return 'not_found';
    if (code === 'BAD_REQUEST' && message.startsWith(PAY_RUN_OVERLAP_PREFIX)) {
      return 'pay_run_overlap';
    }
    if (code === 'BAD_REQUEST' && message.startsWith(TERMINAL_STATUS_PREFIX)) {
      return 'already_terminal';
    }
  }
  return 'error';
}

export type LeaveCancelResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: LeaveCancelFailure };

export interface LeaveCancelDeps {
  cancel: (input: LeaveFetchInput & { request_id: string }) => Promise<unknown>;
}

function defaultLeaveCancelDeps(): LeaveCancelDeps {
  const client = getTrpcClient();
  return {
    cancel: (input) => client.payroll.cancelLeave.mutate(input),
  };
}

export async function cancelLeaveRequest(
  request_id: string,
  entityInput: LeaveFetchInput,
  deps?: LeaveCancelDeps,
): Promise<LeaveCancelResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveCancelDeps();
    await resolvedDeps.cancel({ ...entityInput, request_id });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave: cancel failed:', err);
    return { outcome: 'failure', failure: classifyCancelFailure(err) };
  }
}

// ── Write path: request (employer-on-behalf) ──────────────────────────────

/**
 * How `payroll.requestLeave` can fail from the employer "record leave"
 * drawer. Separate from `LeaveActionFailure` because the inputs and
 * semantics differ.
 */
export type LeaveRequestFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  /** `BAD_REQUEST: Employee already has … overlapping this date range.` */
  | 'overlap'
  /** `BAD_REQUEST: End date must be on or after start date` */
  | 'invalid_dates'
  /** `BAD_REQUEST: Unknown or inactive leave type …` */
  | 'unknown_leave_type'
  /** `BAD_REQUEST` + `LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE` -- contractors accrue no leave. */
  | 'contractor_not_eligible'
  | 'error';

const OVERLAP_PREFIX =
  'Employee already has a pending or approved leave request overlapping';
const INVALID_DATE_MESSAGE = 'End date must be on or after start date';
const UNKNOWN_LEAVE_TYPE_PREFIX = 'Unknown or inactive leave type';

function classifyRequestFailure(err: unknown): LeaveRequestFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (extractLedgerCode(err) === PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE) {
    return 'contractor_not_eligible';
  }
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: { code?: unknown } }).data;
    const code = data?.code;
    const message = err instanceof Error ? err.message : '';
    if (code === 'NOT_FOUND') return 'not_found';
    if (code === 'BAD_REQUEST' && message.startsWith(OVERLAP_PREFIX)) {
      return 'overlap';
    }
    if (code === 'BAD_REQUEST' && message === INVALID_DATE_MESSAGE) {
      return 'invalid_dates';
    }
    if (
      code === 'BAD_REQUEST' &&
      message.startsWith(UNKNOWN_LEAVE_TYPE_PREFIX)
    ) {
      return 'unknown_leave_type';
    }
  }
  return 'error';
}

export type LeaveRequestResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: LeaveRequestFailure };

export interface RecordLeaveInput {
  employee_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  /** Days × 100. */
  days_requested: number;
  notes?: string;
}

export interface LeaveRequestDeps {
  requestLeave: (input: LeaveFetchInput & RecordLeaveInput) => Promise<unknown>;
}

function defaultLeaveRequestDeps(): LeaveRequestDeps {
  const client = getTrpcClient();
  return {
    requestLeave: (input) => client.payroll.requestLeave.mutate(input),
  };
}

export async function recordLeaveRequest(
  input: RecordLeaveInput,
  entityInput: LeaveFetchInput,
  deps?: LeaveRequestDeps,
): Promise<LeaveRequestResult> {
  try {
    const resolvedDeps = deps ?? defaultLeaveRequestDeps();
    await resolvedDeps.requestLeave({ ...entityInput, ...input });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave: record request failed:', err);
    return { outcome: 'failure', failure: classifyRequestFailure(err) };
  }
}

export interface UseLeaveRequestsResult {
  state: LeaveState;
  /** Convenience projection: `[]` for every non-`ready` state. */
  requests: LeaveRequest[];
  /** Flat employee list for the "Record leave" employee picker — same list used for name resolution. */
  employees: LeaveEmployeeLookup[];
  counts: Record<LeaveStatus, number>;
  is_loading: boolean;
  /** Re-runs the fetch from scratch — wired to the `error`/`entitlement_required` states' "try again" action. */
  retry: () => void;
  /** `undefined` when the write path isn't reachable — see module docblock's "Approve/Reject write path" section. */
  approve: ((request_id: string) => Promise<LeaveActionResult>) | undefined;
  /** `undefined` when the write path isn't reachable. `rejection_reason` is optional on the backend. */
  reject:
    | ((
        request_id: string,
        rejection_reason: string | undefined,
      ) => Promise<LeaveActionResult>)
    | undefined;
  /**
   * Cancel a pending or approved leave request. `undefined` when the write
   * path isn't reachable. On success, re-fetches the queue automatically.
   */
  cancel: ((request_id: string) => Promise<LeaveCancelResult>) | undefined;
  /**
   * Record (submit) a leave request on behalf of an employee. `undefined`
   * when the write path isn't reachable. On success, re-fetches the queue.
   */
  request:
    | ((input: RecordLeaveInput) => Promise<LeaveRequestResult>)
    | undefined;
  /**
   * Monotonic counter bumped after every SUCCESSFUL approve / reject /
   * cancel / record. Consumers that derive data from leave requests but
   * fetch it themselves (the Balances tab's pending/available columns) key
   * their own re-fetch on this, so a mutation never leaves them stale.
   */
  mutation_version: number;
}

const EMPTY_COUNTS: Record<LeaveStatus, number> = {
  pending: 0,
  approved: 0,
  rejected: 0,
  cancelled: 0,
};

/** Leave requests plus the approve/reject actions the queue screen needs — the real, self-fetching hook. */
export function useLeaveRequests(): UseLeaveRequestsResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [requests, setRequests] = React.useState<LeaveRequest[] | undefined>(
    undefined,
  );
  const [employees, setEmployees] = React.useState<LeaveEmployeeLookup[]>([]);
  const [failure, setFailure] = React.useState<LeaveLoadFailure | undefined>(
    undefined,
  );
  const [reloadToken, setReloadToken] = React.useState(0);
  const [mutationVersion, setMutationVersion] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setRequests(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const fetched = await fetchLeaveRequestsBundle(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (fetched.outcome === 'success') {
        setRequests(fetched.requests);
        setEmployees(fetched.employees);
      } else {
        setFailure(fetched.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = resolveLeaveState({ entityContext, requests, failure });

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  // A successful mutation changes pending/approved totals, so re-fetch the
  // queue AND signal balance consumers via `mutation_version`.
  const onMutated = React.useCallback(() => {
    setReloadToken((token) => token + 1);
    setMutationVersion((version) => version + 1);
  }, []);

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const approve = canWrite
    ? (request_id: string): Promise<LeaveActionResult> =>
        approveLeaveRequest(request_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') onMutated();
          return actionResult;
        })
    : undefined;

  const reject = canWrite
    ? (
        request_id: string,
        rejection_reason: string | undefined,
      ): Promise<LeaveActionResult> =>
        rejectLeaveRequest(request_id, rejection_reason, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') onMutated();
          return actionResult;
        })
    : undefined;

  const cancel = canWrite
    ? (request_id: string): Promise<LeaveCancelResult> =>
        cancelLeaveRequest(request_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((result) => {
          if (result.outcome === 'success') onMutated();
          return result;
        })
    : undefined;

  const request = canWrite
    ? (input: RecordLeaveInput): Promise<LeaveRequestResult> =>
        recordLeaveRequest(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((result) => {
          if (result.outcome === 'success') onMutated();
          return result;
        })
    : undefined;

  return {
    state,
    requests: state.status === 'ready' ? state.requests : [],
    employees,
    counts:
      state.status === 'ready' ? count_by_status(state.requests) : EMPTY_COUNTS,
    is_loading: state.status === 'loading',
    retry,
    approve,
    reject,
    cancel,
    request,
    mutation_version: mutationVersion,
  };
}
